import { useState, type FC } from "react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import SessionStorageHelper from "../../utils/sessionStorageHelper";

const Workoutpage: FC = () => {
  const navigate = useNavigate();
  const [selectedActivity, setSelectedActivity] = useState<string>("");

  const handleNext = () => {
    // Convert to Django choice keys
    let backendValue;
    if (selectedActivity === "Sedentary") {
      backendValue = "sedentary";
    } else if (selectedActivity === "Lightly active") {
      backendValue = "lightly_active";
    } else if (selectedActivity === "Moderately active") {
      backendValue = "moderately_active";
    } else if (selectedActivity === "Very active") {
      backendValue = "very_active";
    }

    // Save physical activity level to session storage
    SessionStorageHelper.saveField("activity_level", backendValue);

    // Navigate to the next assessment page (Diet page)
    navigate("/glucera" + GluceraNavigationRoutes.DIETPAGE);
  };

  const activities = [
    {
      value: "Sedentary",
      desc: "Mostly sitting",
    },
    {
      value: "Lightly active",
      desc: "3-5 walks or light workouts/week",
    },
    {
      value: "Moderately active",
      desc: "Exercise 3-5x/week, mix of cardio & strength",
    },
    {
      value: "Very active",
      desc: "6+ high-intensity sessions/week",
    },
  ];

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header */}
        <div className="space-y-4 p-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-orange-400">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-muted-foreground">
              Bite Balance
            </span>
          </div>

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 8 of 12
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-2">
          <div className="mb-6 space-y-2">
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
              How Physically Active Are You?
            </h1>
            <p className="text-sm text-muted-foreground">
              This helps us understand how your activity impacts blood sugar
            </p>
          </div>

          <div className="space-y-3">
            {activities.map(option => (
              <Card
                key={option.value}
                className={`cursor-pointer transition-all ${
                  selectedActivity === option.value
                    ? "border-2 border-primary bg-primary/5"
                    : "border-2 border-border hover:border-primary/50"
                }`}
                onClick={() => setSelectedActivity(option.value)}
              >
                <div className="flex items-start gap-3 p-4">
                  <div
                    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                      selectedActivity === option.value
                        ? "border-primary bg-primary"
                        : "border-muted-foreground"
                    }`}
                  >
                    {selectedActivity === option.value && (
                      <div className="h-2.5 w-2.5 rounded-full bg-primary-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="cursor-pointer text-base font-semibold text-foreground">
                      {option.value}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {option.desc}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Footer Button */}
        <div className="pb-safe border-t p-4">
          <Button
            onClick={handleNext}
            disabled={!selectedActivity}
            className="h-11 w-full rounded-xl bg-gradient-primary text-sm hover:opacity-90 disabled:opacity-50 sm:h-12 sm:text-base"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Workoutpage;
